import { zodResolver } from "@hookform/resolvers/zod";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LockIcon from "@mui/icons-material/Lock";
import SaveIcon from '@mui/icons-material/Save';
import {
  Box, Button, CircularProgress,
  FormControl,
  Grid2, IconButton, InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { autoFillHydrogeologicContext } from "../../api/captures/hydrogeologicContext";
import { useReferenceData } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../hooks/useFormStyles";
import {
  CaptureInfoApiPayload,
  UpdateCaptureInfoForm,
  getUpdateCaptureInfoFormSchema,
} from "../../types/forms/capture";
import { CaptureResponse } from "../../types/model/capture";
import { CaptureLocationResponse } from "../../types/model/captureLocation";
import { CapturePurpose } from "../../types/model/capturePurpose";
import { ConstructionDetails } from "../../types/model/constructionDetails";
import { HydrogeologicContextResponse } from "../../types/model/hydrogeologicContext";
import ConfirmationDialog from "../General/ConfirmationDialog";
import FormSelect from "../General/FormAutocomplete";

interface CaptureInfoProps {
  canEdit: boolean;
  capture: CaptureResponse | undefined;
  captureLocation: CaptureLocationResponse | undefined;
  capturePurposes: CapturePurpose | undefined;
  constructionDetails: ConstructionDetails | undefined;
  hydrogeologicContext: HydrogeologicContextResponse | undefined;
  onSubmit: (values: CaptureInfoApiPayload) => Promise<void>;
  crs: "wgs84" | "etrs89";
  onCrsChange: (newCrs: "wgs84" | "etrs89") => void;
  onDataRefetch?: () => void;
  loadingLocation?: boolean;
}

export default function CaptureInfo({
  canEdit,
  capture,
  captureLocation,
  capturePurposes,
  constructionDetails,
  hydrogeologicContext,
  onSubmit,
  crs,
  onCrsChange,
  onDataRefetch,
  loadingLocation,
}: Readonly<CaptureInfoProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
  const formStyles = useFormStyles(isEditing);
  const validationSchema = getUpdateCaptureInfoFormSchema(t);

  const getInitialZValue = (val: number | undefined | null) => (val === 0 ? null : val ?? undefined);
  const getInitialElevationValue = (val: number | undefined | null) => (val === 0 ? null : val ?? undefined);
  const otherDesignationsToArray = (obj: Record<string, string> | undefined | null) =>
    Object.entries(obj ?? {}).map(([label, value]) => ({ label, value }));

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      capture: {
        name: capture?.name,
        entityName: capture?.entityName,
        entityAcronym: capture?.entityAcronym,
        managementUnitName: capture?.managementUnitName,
        aquaEpsDesignation: capture?.aquaEpsDesignation,
        entityDesignation: capture?.entityDesignation,
        licensingDesignation: capture?.licensingDesignation,
        licensingDesignationSystem: capture?.licensingDesignationSystem ?? null,
        otherDesignations: otherDesignationsToArray(capture?.otherDesignations),
      },
      constructionDetails: {
        subsurfaceCaptureTypeId:
        constructionDetails?.subsurfaceCaptureTypeId ?? null,
        drillingMethodId: constructionDetails?.drillingMethodId ?? null,
        boreholeDepth: constructionDetails?.boreholeDepth ?? null,
        casingDepth: constructionDetails?.casingDepth ?? null,
        casingDiameter: constructionDetails?.casingDiameter ?? null,
        casingMaterialId: constructionDetails?.casingMaterialId ?? null,
        firstDrainDepth: constructionDetails?.firstDrainDepth ?? null,
        lastDrainDepth: constructionDetails?.lastDrainDepth ?? null,
        constructionYear: constructionDetails?.constructionYear ?? null,
        boreholeDiameter: constructionDetails?.boreholeDiameter ?? null,
      },
      capturePurposes: {
        supplyTypeId: capturePurposes?.supplyTypeId ?? null,
        purposeId: capturePurposes?.purposeId ?? null,
        usageId: capturePurposes?.usageId ?? null,
      },
      captureLocation: {
        mapReference: captureLocation?.mapReference,
        coordinates:
          captureLocation?.coordinates?.crs === "ETRS89"
            ? {
                crs: "ETRS89",
                xETRS: captureLocation.coordinates.xETRS ?? undefined,
                yETRS: captureLocation.coordinates.yETRS ?? undefined,
                zETRS: getInitialZValue(captureLocation.coordinates.zETRS),
              }
            : {
                crs: "WGS84",
                longitude: captureLocation?.coordinates?.longitude ?? undefined,
                latitude: captureLocation?.coordinates?.latitude ?? undefined,
                elevation: getInitialElevationValue(captureLocation?.coordinates?.elevation),
              },
      },
      hydrogeologicContext: {
        watershedId: hydrogeologicContext?.watershedId ?? null,
        subWatershedId: hydrogeologicContext?.subWatershedId ?? null,
        hydrogeologicUnitId: hydrogeologicContext?.hydrogeologicUnitId ?? null,
        aquiferSystemId: hydrogeologicContext?.aquiferSystemId ?? null,
        aquiferUnitId: hydrogeologicContext?.aquiferUnitId ?? null,
        geologyMassId: hydrogeologicContext?.geologyMassId ?? null,
        waterBodyId: hydrogeologicContext?.waterBodyId ?? null,
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "capture.otherDesignations",
  });

  const { data: subsurfaceCaptureTypes = [] } = useReferenceData(
    "construction",
    "subsurface_captures_types"
  );
  const { data: supplyTypes = [] } = useReferenceData("usage", "supply_types");
  const { data: purposes = [] } = useReferenceData("usage", "purposes");
  const { data: usages = [] } = useReferenceData("usage", "usages");
  const { data: drillingMethods = [] } = useReferenceData("construction", "drilling_methods");
  const { data: casingMaterials = [] } = useReferenceData("construction", "casing_materials");
  const { data: aquiferUnits = [] } = useReferenceData("hydro", "aquifer_units");
  const { data: geologyMasses = [] } = useReferenceData("geology", "geological_masses");
  const { data: watersheds = [] } = useReferenceData("hydro", "watersheds");
  const { data: subWatersheds = [] } = useReferenceData("hydro", "sub_watersheds");
  const { data: hydrogeologicUnits = [] } = useReferenceData("hydro", "hydrogeological_units");
  const { data: waterBodies = [] } = useReferenceData("hydro", "water_bodies");
  const { data: aquiferSystems = [] } = useReferenceData("hydro", "aquifer_systems");

  const handleConfirmAutoFill = async () => {
    if (capture?.id) {
      try {
        await autoFillHydrogeologicContext(capture.id);
        if (onDataRefetch) {
          onDataRefetch();
        }
      } catch (error) {
        handleError(error);
      }
    } else {
      enqueueSnackbar(
        t("generic.errors.capture-id-missing"),
        { variant: "error" }
      );
    }
    setIsConfirmationDialogOpen(false);
  };

  const handleFormSubmit = async (values: UpdateCaptureInfoForm) => {
    try {
      const payload = {
        ...values,
        capture: {
          ...values.capture,
          otherDesignations: Object.fromEntries(
            values.capture.otherDesignations.map((item) => [item.label, item.value])
          ),
        },
      };
      const dirtyCoordinates = methods.formState.dirtyFields.captureLocation?.coordinates;
      const coordsDirty =
        dirtyCoordinates &&
        (('xETRS' in dirtyCoordinates && dirtyCoordinates.xETRS) ||
          ('yETRS' in dirtyCoordinates && dirtyCoordinates.yETRS) ||
          ('longitude' in dirtyCoordinates && dirtyCoordinates.longitude) ||
          ('latitude' in dirtyCoordinates && dirtyCoordinates.latitude));

      setIsSaving(true);
      await onSubmit(payload);
      setIsEditing(false);
      setIsSaving(false);

      if (coordsDirty) {
        setIsConfirmationDialogOpen(true);
      }

    } catch (error) {
      handleError(error);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    let resetCoordinates = captureLocation?.coordinates;

    if (resetCoordinates) {
      if (resetCoordinates.crs === "ETRS89") {
        resetCoordinates = {
          ...resetCoordinates,
          zETRS: getInitialZValue(resetCoordinates.zETRS),
        };
      } else if (resetCoordinates.crs === "WGS84") {
        resetCoordinates = {
          ...resetCoordinates,
          elevation: getInitialElevationValue(resetCoordinates.elevation),
        };
      }
    }

    methods.reset({
      capture: {
        ...capture,
        otherDesignations: otherDesignationsToArray(capture?.otherDesignations), // 👈
      },
      captureLocation: captureLocation ? { ...captureLocation, coordinates: resetCoordinates } : undefined,
      capturePurposes: { ...capturePurposes },
      constructionDetails: { ...constructionDetails },
      hydrogeologicContext: { ...hydrogeologicContext },
    });
  }, [
    capture,
    captureLocation,
    capturePurposes,
    constructionDetails,
    hydrogeologicContext,
  ]);

  const locationKey =
    crs === "etrs89"
      ? "captureLocation.coordinates.zETRS"
      : "captureLocation.coordinates.elevation";

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(handleFormSubmit)}
      >
        {/* Main Form */}
        <Grid2 container spacing={2}>
          {/* Identification */}
          <Grid2 sx={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography variant="h5">
                {t("captures.capture.general.title")}
              </Typography>

              {/* Action Icons */}
              {isEditing && canEdit ? ( 
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("generic.save")}>
                    <IconButton
                      color="success"
                      onClick={methods.handleSubmit(handleFormSubmit)}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("generic.cancel")}>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setIsEditing(false);
                        methods.reset();
                      }}
                    >
                      <EditOffIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                canEdit && (
                  <Tooltip title={t("generic.edit")}>
                    <IconButton
                      color="primary"
                      onClick={() => setIsEditing(true)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </Box>

            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6">{t("captures.capture.general.identification.title")}</Typography>
                  {isEditing ? null : <LockIcon color="action" />}
                </Box>
                <Grid2 container spacing={2} mt={1}>
                  {/* Entity Identification */}
                  <Grid2 size={12}>
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mt: 2 }}
                    >
                      {t("captures.capture.general.identification.entity-section")}
                    </Typography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.identification.entity-name"
                      )}
                      variant="standard"
                      disabled
                      sx={{
                        ...formStyles,
                        "& .MuiInputBase-input": {
                          textTransform: "uppercase",
                        },
                      }}
                      {...methods.register("capture.entityName")}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.identification.entity-acronym"
                      )}
                      variant="standard"
                      disabled
                      sx={formStyles}
                      {...methods.register("capture.entityAcronym")}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.identification.management-unit"
                      )}
                      variant="standard"
                      disabled
                      sx={formStyles}
                      {...methods.register("capture.managementUnitName")}
                    />
                  </Grid2>

                  {/* Capture Identification */}
                  <Grid2 size={12} mt={1}>
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mt: 2 }}
                    >
                      {t("captures.capture.general.identification.capture-section")}
                    </Typography>
                  </Grid2>
                  
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.identification.name"
                      )}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isSaving}
                      sx={formStyles}
                      {...methods.register("capture.name")}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.identification.entity-desig"
                      )}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isSaving}
                      sx={formStyles}
                      {...methods.register("capture.entityDesignation")}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="capture.licensingDesignationSystem"
                      control={methods.control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={{ ...formStyles }}
                          error={!!methods.formState.errors.capture?.licensingDesignationSystem}
                        >
                          <InputLabel>
                            {t("captures.capture.general.identification.licensing-system")}
                          </InputLabel>
                          <Select
                            {...field}
                            value={field.value ?? ""}
                            label={t("captures.capture.general.identification.licensing-system")}
                            onChange={(e) => field.onChange(e.target.value || null)}
                          >
                            <MenuItem value="SNIRH">SNIRH</MenuItem>
                            <MenuItem value="TUA">TUA</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t(
                        "captures.capture.general.identification.licensing-designation"
                      )}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isSaving}
                      sx={{ ...formStyles }}
                      {...methods.register("capture.licensingDesignation")}
                      error={!!methods.formState.errors.capture?.licensingDesignation}
                      helperText={methods.formState.errors.capture?.licensingDesignation?.message}
                    />
                  </Grid2>  

                  <Grid2 size={12} mt={1} mb={1}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 600, letterSpacing: 0.5 }}
                      >
                        {t("captures.capture.general.identification.other-designations.section")}
                      </Typography>
                      {isEditing && fields.length < 20 && (
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          disabled={isSaving}
                          onClick={() => append({ label: "", value: "" })}
                        >
                          {t("captures.capture.general.identification.other-designations.add-new")}
                        </Button>
                      )}
                    </Box>
                  </Grid2>

                  {fields.length === 0 && (
                    <Grid2 size={12}>
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic", py: 0.5 }}>
                        {isEditing
                          ? t("captures.capture.general.identification.other-designations.empty-editing")
                          : t("captures.capture.general.identification.other-designations.empty")}
                      </Typography>
                    </Grid2>
                  )}

                  {fields.map((field, index) => (
                    <Grid2 size={12} key={field.id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1.5,
                          mb: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <TextField
                          fullWidth
                          label={t("captures.capture.general.identification.other-designation.label")}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={formStyles}
                          {...methods.register(`capture.otherDesignations.${index}.label`)}
                          error={!!methods.formState.errors.capture?.otherDesignations?.[index]?.label}
                          helperText={methods.formState.errors.capture?.otherDesignations?.[index]?.label?.message}
                        />

                        <ArrowForwardIcon fontSize="small" sx={{ color: "text.disabled", flexShrink: 0 }} />

                        <TextField
                          fullWidth
                          label={t("captures.capture.general.identification.other-designation.value")}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={formStyles}
                          {...methods.register(`capture.otherDesignations.${index}.value`)}
                          error={!!methods.formState.errors.capture?.otherDesignations?.[index]?.value}
                          helperText={methods.formState.errors.capture?.otherDesignations?.[index]?.value?.message}
                        />

                        {isEditing && (
                          <IconButton
                            type="button"
                            disabled={isSaving}
                            onClick={() => remove(index)}
                            aria-label={t("generic.remove")}
                            sx={{ flexShrink: 0 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Grid2>
                  ))}

                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormSelect
                      name="constructionDetails.subsurfaceCaptureTypeId"
                      label={t("captures.capture.general.identification.subsurface-capture")}
                      options={subsurfaceCaptureTypes}
                      isEditing={isEditing}
                      isSaving={isSaving}
                      disabled={false}
                      formStyles={formStyles}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormSelect
                      name="capturePurposes.supplyTypeId"
                      label={t("captures.capture.general.identification.supply-type")}
                      options={supplyTypes}
                      isEditing={isEditing}
                      isSaving={isSaving}
                      disabled={false}
                      formStyles={formStyles}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormSelect
                      name="capturePurposes.purposeId"
                      label={t("captures.capture.general.identification.purpose")}
                      options={purposes}
                      isEditing={isEditing}
                      isSaving={isSaving}
                      disabled={false}
                      formStyles={formStyles}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormSelect
                      name="capturePurposes.usageId"
                      label={t("captures.capture.general.identification.usage")}
                      options={usages}
                      isEditing={isEditing}
                      isSaving={isSaving}
                      disabled={false}
                      formStyles={formStyles}
                    />
                  </Grid2>
                </Grid2>
              </Box>
            </Paper>
          </Grid2>
          
          <Grid2 container spacing={2} mb={7}>
            <Grid2 size={{ xs: 12, md: 6 }}>    
              {/* Location Details*/}
              <Grid2
                container
                direction="column"
                spacing={2}
                sx={{ height: "100%" }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="h6">{t("captures.capture.general.location-details.title")}</Typography>
                      {isEditing ? null : <LockIcon color="action" />}
                    </Box>
                    {loadingLocation ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 4,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                          mb={2}
                          gap={2}
                        >
                          <ToggleButtonGroup
                            value={crs}
                            exclusive
                            onChange={(_, newCrs) =>
                              newCrs && onCrsChange(newCrs)
                            }
                            size="small"
                            color="primary"
                          >
                            <ToggleButton value="etrs89">ETRS89</ToggleButton>
                            <ToggleButton value="wgs84">WGS84</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>

                        <Box mt={1}>
                          <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{ fontWeight: 600, mt: 2 }}
                          >
                            {t(
                              "captures.capture.general.location-details.reference-point"
                            )}
                          </Typography>

                          {crs === "etrs89" ? (
                            <Grid2 container spacing={2}>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label={`${t("captures.capture.general.location-details.x-etrs")} *`}                           
                                  variant={isEditing ? "outlined" : "standard"}
                                  disabled={!isEditing || isSaving}
                                  sx={{ ...formStyles }}
                                  {...methods.register(
                                    "captureLocation.coordinates.xETRS",
                                    { valueAsNumber: true }
                                  )}
                                  slotProps={{
                                    inputLabel: {
                                      shrink: true,
                                    },
                                    input: {
                                      endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                                    },
                                  }}
                                  error={!!(methods.formState.errors.captureLocation?.coordinates as any)?.xETRS}
                                  helperText={(methods.formState.errors.captureLocation?.coordinates as any)?.xETRS?.message}
                                />
                              </Grid2>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label={`${t("captures.capture.general.location-details.y-etrs")} *`}
                                  variant={isEditing ? "outlined" : "standard"}
                                  disabled={!isEditing || isSaving}
                                  sx={{ ...formStyles }}
                                  {...methods.register(
                                    "captureLocation.coordinates.yETRS",
                                    { valueAsNumber: true }
                                  )}
                                  slotProps={{
                                    inputLabel: {
                                      shrink: true,
                                    },
                                    input: {
                                      endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                                    },
                                  }}
                                  error={!!(methods.formState.errors.captureLocation?.coordinates as any)?.yETRS}
                                  helperText={(methods.formState.errors.captureLocation?.coordinates as any)?.yETRS?.message}
                                />
                              </Grid2>
                            </Grid2>
                          ) : (
                            <Grid2 container spacing={2}>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label={`${t("captures.capture.general.location-details.longitude")} *`}
                                  variant={isEditing ? "outlined" : "standard"}
                                  disabled={!isEditing || isSaving}
                                  sx={{ ...formStyles }}
                                  {...methods.register(
                                    "captureLocation.coordinates.longitude",
                                    { valueAsNumber: true }
                                  )}
                                  slotProps={{
                                    inputLabel: {
                                      shrink: true,
                                    },
                                    input: {
                                      endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                    },
                                  }}
                                  error={!!(methods.formState.errors.captureLocation?.coordinates as any)?.longitude}
                                  helperText={(methods.formState.errors.captureLocation?.coordinates as any)?.longitude?.message}
                                />
                              </Grid2>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  label={`${t("captures.capture.general.location-details.latitude")} *`}
                                  variant={isEditing ? "outlined" : "standard"}
                                  disabled={!isEditing || isSaving}
                                  sx={{ ...formStyles }}
                                  {...methods.register("captureLocation.coordinates.latitude", { valueAsNumber: true })}
                                  slotProps={{
                                    inputLabel: {
                                      shrink: true,
                                    },
                                    input: {
                                      endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                    },
                                  }}
                                  error={!!(methods.formState.errors.captureLocation?.coordinates as any)?.latitude}
                                  helperText={(methods.formState.errors.captureLocation?.coordinates as any)?.latitude?.message}
                                />
                              </Grid2>
                            </Grid2>
                          )}
                        </Box>

                        {/* Elevation / Z */}
                        <Box mt={3}>
                          <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{ fontWeight: 600, mt: 2 }}
                          >
                            {t(
                              "captures.capture.general.location-details.elevation"
                            )}
                          </Typography>
                          <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, sm: 4 }}>
                              <TextField
                                fullWidth
                                label={
                                  crs === "etrs89"
                                    ? t("captures.capture.general.location-details.z-etrs")
                                    : t("captures.capture.general.location-details.elevation")
                                }
                                variant={isEditing ? "outlined" : "standard"}
                                disabled={!isEditing || isSaving}
                                sx={{ ...formStyles }}
                                type="number"
                                {...methods.register(locationKey)}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                  input: {
                                    endAdornment: (
                                      <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                                    ),
                                  },
                                }}
                              />
                            </Grid2>
                          </Grid2>
                        </Box>


                        {/* Map Reference */}
                        <Box mt={3}>
                          <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                label={t(
                                  "captures.capture.general.location-details.map-reference"
                                )}
                                variant={isEditing ? "outlined" : "standard"}
                                disabled={!isEditing || isSaving}
                                sx={{ ...formStyles }}
                                {...methods.register(
                                  "captureLocation.mapReference"
                                )}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  }
                                }}
                              />
                            </Grid2>
                          </Grid2>
                        </Box>
                      </>
                    )}
                  </Box>
                </Paper>
              </Grid2>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              {/* Geological and Hydro Context*/}
              <Grid2
                container
                direction="column"
                spacing={2}
                sx={{ height: "100%" }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6">{t("captures.capture.general.hydrogeologic-context.title")}</Typography>
                        {isEditing ? null : <LockIcon color="action" />}
                        <Tooltip title={t("captures.capture.general.location-details.autofill")}>
                          <IconButton onClick={() => setIsConfirmationDialogOpen(true)}>
                            <AutoFixHighIcon color="primary"/>
                          </IconButton>
                        </Tooltip>
                        {hydrogeologicContext?.isManuallyInserted && (
                          <Tooltip title={t('captures.capture.general.hydrogeologic-context.manual-selection')}>
                            <FactCheckIcon color="action" />
                          </Tooltip>
                        )}
                      </Box>
                      <Grid2 container spacing={2} mt={1}>
                        <Grid2 size={{ xs: 12 }}>
                          <FormSelect
                            name="hydrogeologicContext.watershedId"
                            label={t("captures.capture.general.hydrogeologic-context.watershed")}
                            options={watersheds}
                            isEditing={isEditing}
                            isSaving={isSaving}
                            disabled={false}
                            formStyles={formStyles}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                          <FormSelect
                            name="hydrogeologicContext.subWatershedId"
                            label={t("captures.capture.general.hydrogeologic-context.sub-watershed")}
                            options={subWatersheds}
                            isEditing={isEditing}
                            isSaving={isSaving}
                            disabled={false}
                            formStyles={formStyles}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                          <FormSelect
                            name="hydrogeologicContext.hydrogeologicUnitId"
                            label={t("captures.capture.general.hydrogeologic-context.hydrogeogic-unit")}
                            options={hydrogeologicUnits}
                            isEditing={isEditing}
                            isSaving={isSaving}
                            disabled={false}
                            formStyles={formStyles}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                          <FormSelect
                            name="hydrogeologicContext.waterBodyId"
                            label={t("captures.capture.general.hydrogeologic-context.water-body")}
                            options={waterBodies}
                            isEditing={isEditing}
                            isSaving={isSaving}
                            disabled={false}
                            formStyles={formStyles}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                          <FormSelect
                            name="hydrogeologicContext.aquiferSystemId"
                            label={t("captures.capture.general.hydrogeologic-context.aquifer-system")}
                            options={aquiferSystems}
                            isEditing={isEditing}
                            isSaving={isSaving}
                            disabled={false}
                            formStyles={formStyles}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                          <FormSelect
                            name="hydrogeologicContext.aquiferUnitId"
                            label={t("captures.capture.general.hydrogeologic-context.aquifer-unit")}
                            options={aquiferUnits}
                            isEditing={isEditing}
                            isSaving={isSaving}
                            disabled={false}
                            formStyles={formStyles}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                          <FormSelect
                            name="hydrogeologicContext.geologyMassId"
                            label={t("captures.capture.general.hydrogeologic-context.geology-mass")}
                            options={geologyMasses}
                            isEditing={isEditing}
                            isSaving={isSaving}
                            disabled={false}
                            formStyles={formStyles}
                          />
                        </Grid2>
                      </Grid2>
                    </Box>                
                  </Paper>
              </Grid2>
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              {/* Construction Details*/}
              <Grid2
                container
                direction="column"
                spacing={2}
                sx={{ height: "100%" }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="h6">{t("captures.capture.general.construction-details.title")}</Typography>
                      {isEditing ? null : <LockIcon color="action" />}
                    </Box>

                    {/* Drilling */}
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mt: 2 }}
                    >
                      {t(
                        "captures.capture.general.construction-details.drilling"
                      )}
                    </Typography>
                    <Grid2 container spacing={2} mt={1}>
                      <Grid2 size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          label={t(
                            "captures.capture.general.construction-details.depth"
                          )}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={{ ...formStyles }}
                          type="number"
                          {...methods.register("constructionDetails.boreholeDepth")}
                          value={methods.watch("constructionDetails.boreholeDepth") ?? ""}
                          slotProps={{
                            input: {
                              endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          label={t(
                            "captures.capture.general.construction-details.diameter"
                          )}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={{ ...formStyles }}
                          type="number"
                          {...methods.register("constructionDetails.boreholeDiameter")}
                          value={methods.watch("constructionDetails.boreholeDiameter") ?? ""}
                          slotProps={{
                            input: {
                              endAdornment: <InputAdornment position="end">{t("generic.units.mm")}</InputAdornment>,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 5.5 }}>
                        <FormSelect
                          name="constructionDetails.drillingMethodId"
                          label={t("captures.capture.general.construction-details.drilling-method")}
                          options={drillingMethods}
                          isEditing={isEditing}
                          isSaving={isSaving}
                          disabled={false}
                          formStyles={formStyles}
                        />
                      </Grid2>
                    </Grid2>

                    {/* Casing */}
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mt: 2 }}
                    >
                      {t(
                        "captures.capture.general.construction-details.casing"
                      )}
                    </Typography>
                    <Grid2 container spacing={2} mt={1}>
                      <Grid2 size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          label={t(
                            "captures.capture.general.construction-details.depth"
                          )}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={{ ...formStyles }}
                          type="number"
                          {...methods.register("constructionDetails.casingDepth")}
                          value={methods.watch("constructionDetails.casingDepth") ?? ""}
                          slotProps={{
                            input: {
                              endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          label={t(
                            "captures.capture.general.construction-details.diameter"
                          )}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={{ ...formStyles }}
                          type="number"
                          {...methods.register("constructionDetails.casingDiameter")}
                          value={methods.watch("constructionDetails.casingDiameter") ?? ""}
                          slotProps={{
                            input: {
                              endAdornment: <InputAdornment position="end">{t("generic.units.mm")}</InputAdornment>,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 5.5 }}>
                        <FormSelect
                          name="constructionDetails.casingMaterialId"
                          label={t("captures.capture.general.construction-details.casing-material")}
                          options={casingMaterials}
                          isEditing={isEditing}
                          isSaving={isSaving}
                          disabled={false}
                          formStyles={formStyles}
                        />
                      </Grid2>
                    </Grid2>

                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mt: 2 }}
                    >
                      {t(
                        "captures.capture.general.construction-details.drain-location"
                      )}
                    </Typography>
                    <Grid2 container spacing={2} mt={1}>
                      <Grid2 size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          label={t(
                            "captures.capture.general.construction-details.first-drain-depth"
                          )}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={{ ...formStyles }}
                          type="number"
                          {...methods.register("constructionDetails.firstDrainDepth")}
                          value={methods.watch("constructionDetails.firstDrainDepth") ?? ""}
                          slotProps={{
                            input: {
                              endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 3 }}>
                        <TextField
                          fullWidth
                          label={t(
                            "captures.capture.general.construction-details.last-drain-depth"
                          )}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={{ ...formStyles }}
                          type="number"
                          {...methods.register("constructionDetails.lastDrainDepth")}
                          value={methods.watch("constructionDetails.lastDrainDepth") ?? ""}
                          slotProps={{
                            input: {
                              endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label={t(
                            "captures.capture.general.construction-details.construction-year"
                          )}
                          variant={isEditing ? "outlined" : "standard"}
                          disabled={!isEditing || isSaving}
                          sx={{ ...formStyles }}
                          type="number"
                          {...methods.register("constructionDetails.constructionYear")}
                          value={methods.watch("constructionDetails.constructionYear") ?? ""}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                </Paper>
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
      </Box>
      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        title={t("captures.capture.general.hydrogeologic-context.confirm-autofill-title")}
        message={t("captures.capture.general.hydrogeologic-context.confirm-autofill-message")}
        onConfirm={handleConfirmAutoFill}
        onCancel={() => setIsConfirmationDialogOpen(false)}
      />
    </FormProvider>
  );
}
